/*
 * useApiCall provides a unified way to fetch data from APIs with the following features:
 *
 * 1. **In-memory caching for GET requests** with automatic expiration (default 5 minutes).
 *    - Pass `forceRefresh: true` to bypass cache and fetch fresh data.
 *    - Use `invalidateApiCache(path)` to manually clear cached responses after POST/PATCH requests.
 *
 * 2. **Only GET requests are cached** to avoid returning stale data for mutations.
 *
 * 3. **Request cancellation** for ongoing requests to the same endpoint to prevent race conditions,
 *    unless the endpoint is listed in `skipAbortEndpoints`.
 *
 * 4. **Authorization handling via Auth0**:
 *    - Automatically injects access tokens if the request requires authorization.
 */

import { ref, hasInjectionContext } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";
import {
  UseFetchOptions,
  useRuntimeConfig,
  navigateTo,
  useNuxtApp,
} from "#app";
import { $fetch } from "ofetch";
import { useJwtStore } from "@/store/jwt";
import { skipAbortEndpoints } from "@/utils/skipAbortEndpoints";
import { clearSessionData } from "@/utils/SetSessionData";

// Cache setup
const apiCache = new Map<string, any>();
const apiCacheTimestamps = new Map<string, number>();
const EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Track active requests for cancellation
const activeControllers = new Map<string, AbortController>();

// Guard to prevent multiple simultaneous redirects on revoked tokens
let isRedirectingForRevokedToken = false;

type FetchOptions = UseFetchOptions<Response, any>;

const createSuccessResponse = (data: any) => {
  const dataRef = ref(data) as any;
  // Keep _rawValue for compatibility with existing store/component code
  dataRef._rawValue = data;

  return {
    data: dataRef,
    error: ref(null),
    status: ref("success"),
  };
};

const createErrorResponse = (error: any) => ({
  data: ref(null),
  error: ref({
    ...error,
    data: error?.data ?? error?.response?._data,
    statusCode: error?.statusCode ?? error?.status ?? error?.response?.status,
  }),
  status: ref("error"),
});

// To always fetch fresh data after a mutation (e.g. POST), consider adding a forceRefresh argument here to bypass cache
interface ApiCall {
  (
    path: string,
    options?: FetchOptions,
    basePath?: string,
    api?: string,
    skipAuthorization?: boolean,
    forceRefresh?: boolean,
  ): Promise<any>;
}

// Manually clear cache for a specific endpoint
export const invalidateApiCache = (
  path: string,
  api = "rails",
  basePath = "/api/v1",
) => {
  const resolvedBasePath = basePath ?? "/api/v1";
  // If callers provided a plain path only, invalidate any keys that start with this prefix
  const prefix = `${api}:${resolvedBasePath}${path}`;
  for (const key of Array.from(apiCache.keys())) {
    if (key.startsWith(prefix)) {
      apiCache.delete(key);
      apiCacheTimestamps.delete(key);
    }
  }
};

export const useApiCall: ApiCall = async (
  path: string,
  options: UseFetchOptions<Response, any> = {},
  basePath?: string,
  api: string = "rails",
  skipAuthorization: boolean = false,
  forceRefresh: boolean = false,
) => {
  // In production server builds, we bail out early. For tests (NODE_ENV === 'test') allow execution so unit tests can exercise caching.
  if (!import.meta.client && process.env.NODE_ENV !== "test") return;

  const resolvedBasePath = basePath ?? "/api/v1";
  const config = useRuntimeConfig();
  const auth0Composable = hasInjectionContext() ? useAuth0() : null;
  const auth0 =
    auth0Composable && typeof auth0Composable.checkSession === "function"
      ? auth0Composable
      : useNuxtApp().$auth0;
  const jwtStore = useJwtStore();
  const isCMRequest = api === "cm";
  // Build a stable cache key that includes query params and certain headers that can affect responses
  const buildCacheKey = (
    p: string,
    opts: UseFetchOptions<Response, any> | undefined,
  ) => {
    let key = `${api}:${resolvedBasePath}${p}`;

    // Normalize params/searchParams (depending on how callers pass them)
    if (opts) {
      // Use options.params or options.searchParams if present
      // Some callers may put query params in opts.params or opts.searchParams; handle both
      // @ts-ignore
      const rawParams =
        (opts as any).params ?? (opts as any).searchParams ?? null;
      if (rawParams && typeof rawParams === "object") {
        // Copy keys and serialize deterministically (sorted keys)
        const keys = Object.keys(rawParams).sort();
        if (keys.length) {
          const pairs: string[] = [];
          for (const k of keys) {
            const v = rawParams[k];
            // For arrays, sort values for determinism
            if (Array.isArray(v)) {
              pairs.push(
                `${encodeURIComponent(k)}=${encodeURIComponent(
                  String(v.slice().sort().join(",")),
                )}`,
              );
            } else if (v !== undefined && v !== null) {
              pairs.push(
                `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
              );
            }
          }
          if (pairs.length) key += `?${pairs.join("&")}`;
        }
      }

      // Include Accept-Language header if present — it can affect localized responses
      // @ts-ignore
      const hdrs = opts.headers ?? {};
      // prefer header keys in lowercase for lookup
      const hdrKeys = Object.keys(hdrs);
      for (const hk of hdrKeys) {
        if (hk.toLowerCase() === "accept-language") {
          // @ts-ignore
          key += `|lang:${String(hdrs[hk])}`;
          break;
        }
      }
    }

    return key;
  };

  const cacheKey = buildCacheKey(path, options);

  const rawMethod = options.method;
  const method =
    typeof rawMethod === "string" ? rawMethod.toUpperCase() : "GET";
  const isCacheableMethod = method === "GET";

  // Serve from cache if valid and not forced to refresh
  const now = Date.now();
  if (
    isCacheableMethod &&
    !forceRefresh &&
    apiCache.has(cacheKey) &&
    apiCacheTimestamps.has(cacheKey) &&
    now - apiCacheTimestamps.get(cacheKey)! < EXPIRY_MS
  ) {
    const cachedResp = apiCache.get(cacheKey);
    cachedResp.isCached = true;
    return cachedResp;
  }

  // Auth flow: fetch token and update store
  let jwt: string | null = null;
  if (!skipAuthorization && auth0 && typeof auth0.checkSession === "function") {
    await auth0.checkSession();
    if (auth0.isAuthenticated.value) {
      jwt = await auth0.getAccessTokenSilently();
      jwtStore.updateSessionJwt(jwt);
    } else {
      await auth0.loginWithRedirect();
      return;
    }
  }

  // Set baseURL depending on API type
  options.baseURL = isCMRequest
    ? `${window.location.origin}/${resolvedBasePath}`
    : `${config.public.API_HOST}${resolvedBasePath}`;

  // Inject headers if auth is required
  if (!skipAuthorization) {
    let auth = jwtStore.sessionJwt != null ? jwtStore.sessionJwt : jwt;
    if (!isCMRequest) {
      auth = `Bearer ${auth}`;
    }
    options.headers = {
      Authorization: auth || "",
      Source: "Medusa",
    };
  }

  // Cancel previous request for this cacheKey unless endpoint is in skipAbortEndpoints list
  if (!skipAbortEndpoints.includes(path) && activeControllers.has(cacheKey)) {
    activeControllers.get(cacheKey)!.abort();
    activeControllers.delete(cacheKey);
  }

  // Create new controller
  const controller = new AbortController();
  activeControllers.set(cacheKey, controller);
  options.signal = controller.signal;

  try {
    let resp;
    try {
      const data = await $fetch(path, options as any);
      resp = createSuccessResponse(data);
    } catch (error: any) {
      if (error?.name === "AbortError") {
        throw error;
      }
      resp = createErrorResponse(error);
    }

    const errorCode = resp.error.value?.data?.errors?.[0]?.code;
    const statusCode = resp.error.value?.statusCode;

    // Handle revoked token: clear session and redirect to login
    if (statusCode === 401 && errorCode === "revoked_token") {
      // Guard: only the first concurrent call triggers the redirect.
      // Reset after the redirect attempt finishes so future revoked-token
      // responses can still perform the required cleanup.
      if (isRedirectingForRevokedToken) return resp;
      isRedirectingForRevokedToken = true;

      try {
        const loggedInUser =
          localStorage.getItem("logged_in_user_email") || "unknown";
        // eslint-disable-next-line no-console
        console.warn(
          `[LoggedInUser: ${loggedInUser}] Token revoked - clearing session and redirecting to login`,
        );

        // Auth0 session was terminated (backchannel logout or timeout)
        // Clear local state and redirect to login via standard Auth0 flow
        clearSessionData();
        jwtStore.clearSessionJwt();

        // Redirect via standard Auth0 login flow (NOT useLogout)
        const nuxtApp = useNuxtApp();
        const auth0Client = nuxtApp.$auth0;

        if (auth0Client) {
          await auth0Client.loginWithRedirect();
        } else {
          await navigateTo("/", { external: true });
        }
      } finally {
        isRedirectingForRevokedToken = false;
      }
    }

    // Handle 403 errors (except /session_data and no_application_access which are handled by middleware)
    const shouldHandle403 =
      statusCode === 403 &&
      path !== "/session_data" &&
      errorCode !== "no_application_access";

    if (shouldHandle403) {
      clearSessionData();
      jwtStore.clearSessionJwt();

      if (typeof window !== "undefined") {
        await navigateTo("/", { external: true });
      }

      // Throw to prevent caller from processing the invalid response
      throw new Error("Session expired - redirected to login");
    }

    // Store response in cache if applicable
    if (isCacheableMethod && !resp.error.value) {
      apiCache.set(cacheKey, resp);
      apiCacheTimestamps.set(cacheKey, now);
    }

    return resp;
  } catch (err: any) {
    if (err.name === "AbortError") {
      // eslint-disable-next-line no-console
      console.warn(`Request aborted: ${cacheKey}`);
      return null;
    }
    throw err;
  } finally {
    activeControllers.delete(cacheKey);
  }
};
